import { ContentSegmentationDropdown, SearchInput } from "components";
import PeopleVideoCard from "components/cards/VideoCard/People/PeopleVideoCard";
import { type CardsViewerItem } from "components/cardsViewer/types";
import { PeopleEventCard, PeopleFlowCard } from "components/contentAssignment/cards";
import { PeopleEmbeddedVideoCard } from "../../../../../components/contentAssignment/cards/peopleEmbeddedVideoCard/PeopleEmbeddedVideoCard";
import { PeopleScormCard } from "../../../../../components/contentAssignment/cards/peopleScormCard/PeopleScormCard";
import { PeoplePdfCard } from "components/contentAssignment/cards/peoplePdfCard/PeoplePdfCard";
import {
  AssessmentFilterForm,
  EmbeddedVideosFilterForm,
  FlowFilterForm,
  LinkFilterForm,
  PdfsFilterForm,
  SurveyFilterForm,
  VideosFilterForm,
} from "components/filterForms";
import { EventFilterForm } from "components/filterForms/EventFilterForm/EventFilterForm";
import { ContentTypesEnum, RouteNames, SortingDirection, ViewType } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import {
  RemoveAllFlowGroupPrioritySuccess,
  SetAllFlowTemplateGroupPrioritySuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { PeopleType } from "features/Library/PeopleAssignments/types";
import { useReloadItemViewListItems } from "hooks/useReloadItemViewListItems";
import { useRtn } from "hooks/useRtn";
import { bindAction } from "interfaces";
import { type ColumnOption } from "interfaces/columnOptions";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "interfaces/onSelectionChanged";
import { isEmpty, noop } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type NavigateFunction, useNavigate } from "react-router";
import { type FiltersMap } from "utils/filterUtils";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import AssessmentsNoResults from "views/library/assessments/Overview/AssessmentsNoResults";
import EmbeddedVideosNoResults from "views/library/embeddedVideos/contentAssignment/EmbeddedVideosNoResults";
import EventsNoResults from "views/library/events/Overview/EventsNoResults";
import FlowsNoResults from "views/library/flows/flowsOverview/FlowsNoResults";
import PdfsNoResults from "views/library/pdfs/contentAssignment/PdfsNoResults";
import SurveysNoResults from "views/library/surveys/Overview/SurveysNoResults";
import { VideoAssetsNoResults } from "views/library/videos/VideoAssetsNoResults/VideoAssetsNoResults";
import { resetAppliedFilter, resetFilter, setAppliedFilter, setSearch } from "../../state/TemplateContentFiltersSlice";
import { reset, resetItems, setSelected } from "../../state/TemplateContentSlice";
import { fetchTemplateContentFilters } from "../../state/thunks/TemplateContentFiltersThunk";
import { fetchTemplateContent } from "../../state/thunks/TemplateContentThunk";
import {
  ContentColumns,
  type ContentItem,
  type FlowItem,
  type SelectedContentMap,
  type TemplateContent,
  type VideoItem,
} from "../../types/models";
import { getColumnOptions, getFlowColumnOptions } from "./getColumnOptions";
import { ExternalLinksNoResults } from "features/Library/ExternalLinks/Overview/ExternalLinksNoResults";
import ScormFilterForm from "../../../../../components/filterForms/ScormFilterForm/ScormFilterForm";
import type { ContentAssignmentModelItem } from "../../../types";
import ScormsNoResults from "../../../../../views/library/scorms/contentAssignment/ScormsNoResults";

const searchInputPlaceholder: {
  [key in ContentTypesEnum]?: string;
} = {
  Embedded: ContentTypesEnum["Embedded Videos"],
  Uploaded: ContentTypesEnum.Videos,
};

type Props = PropsFromRedux & {
  templateId: number;
  contentMap: SelectedContentMap;
  contextMenuButtonHandlers: {
    onEditPriority: (item: FlowItem) => void;
    onClearPriority: (ids: number[]) => void;
  };
  disabled: boolean;
};

const rtnEvents = [SetAllFlowTemplateGroupPrioritySuccess, RemoveAllFlowGroupPrioritySuccess];

export const PeopleContent = (props: Props) => {
  const {
    items,
    isLoading,
    itemsCount,
    resetOverview,
    resetItems,
    onSearchChanged,
    applyFilter,
    resetAppliedFilter,
    resetFilterState,
    filters,
    fetchContent,
    fetchFilters,
    templateId,
    areAllLoaded,
    selected,
    contentMap,
    setSelectedIds,
    contextMenuButtonHandlers,
    disabled,
  } = props;
  const [selectedViewType, setSelectedViewType] = useState<ViewType>(ViewType.GRID);
  const [gridSortBy, setGridSortBy] = useState<[string, SortingDirection]>([...defaultGridSorting]);
  const isGridView = selectedViewType === ViewType.GRID;
  const [context, setContext] = useState(ContentTypesEnum.Flows);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      resetFilterState();
      resetOverview();
      contentMap.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetch = (skip: number, top: number, sortBy: string, sortOrder: SortingDirection, fromStart = false) => {
    if (isGridView) {
      fetchGrid(fromStart);
    } else {
      fetchContent(templateId, context, skip, top, sortBy, sortOrder, fromStart);
    }
  };

  const fetchGrid = useCallback(
    (fromStart: boolean, sorting?: [string, SortingDirection]) => {
      const [sortBy, sortOrder] = sorting ?? gridSortBy;
      fetchContent(templateId, context, fromStart ? 0 : items.length, 30, sortBy, sortOrder, !fromStart);
    },
    [context, fetchContent, gridSortBy, items.length, templateId],
  );

  const refreshContentGrid = useCallback(() => {
    if (isGridView) {
      resetItems();
      fetchGrid(true);
    }
  }, [isGridView, resetItems, fetchGrid]);

  useRtn(rtnEvents, refreshContentGrid);

  const fetchListView = (
    skip: number = 0,
    top: number = 10,
    sortingColumnName: string = ContentColumns.Title,
    sortingDirection: SortingDirection = SortingDirection.Descending,
  ) => {
    const sortBy = columnToParamMap[sortingColumnName.toLowerCase() as Lowercase<ContentColumns>];
    handleFetch(skip, top, sortBy, sortingDirection);
  };

  const [setReloadListItems, handleSearchChange] = useReloadItemViewListItems((term) => {
    onSearchChanged(term);
    if (isGridView) {
      resetItems();
      fetchGrid(true);
    }
  });

  const onSortChange = (_: React.SyntheticEvent<HTMLElement>, data: { value: [string, SortingDirection] }) => {
    resetItems();
    setGridSortBy(data.value);
    fetchGrid(true, data.value);
  };

  const onViewTypeChange = (viewType: ViewType) => {
    setSelectedViewType(viewType);
    resetItems();
    if (viewType === ViewType.GRID) {
      fetchGrid(true);
    }
  };

  const onSelectedItemsChanged = (ids: number[]) => {
    setSelectedIds({ context, selected: ids });
  };

  const onSelectionChanged = (args: SelectionChangedArgs<TemplateContent>) => {
    const onAdded = (item: TemplateContent) => {
      contentMap?.set(item.id, item);
    };

    const onRemoved = (item: TemplateContent) => {
      contentMap?.delete(item.id);
    };
    HandleOnSelectionChanged(args, onAdded, onRemoved);
  };

  const handleApplyFilter = (filter: FiltersMap) => {
    applyFilter(filter);
    resetItems();
    if (isGridView) {
      fetchGrid(true);
    }
  };

  const handleResetFilter = () => {
    resetAppliedFilter();
    resetItems();
    if (isGridView) {
      fetchGrid(true);
    }
  };

  const handleFiltersFetch = () => {
    fetchFilters(context);
  };

  const dropdown = (
    <ContentSegmentationDropdown
      onContextChange={(ctx: ContentTypesEnum) => {
        setSelectedViewType((prev) => {
          return contentPropsPovider(ctx, navigate, selected, contextMenuButtonHandlers, disabled).renderCard
            ? prev
            : ViewType.LIST;
        });
        resetOverview();
        resetFilterState();
        setContext(ctx);
        contentMap.clear();
      }}
      defaultValue={context}
    />
  );

  const {
    getNoResults,
    getColumnOption,
    filterForm: FilterForm,
    renderCard,
  } = useMemo(
    () => contentPropsPovider(context, navigate, selected, contextMenuButtonHandlers, disabled),
    [context, navigate, selected, contextMenuButtonHandlers, disabled],
  );
  const noResults = useMemo(
    () => getNoResults(!isEmpty(filters.appliedFilter) || !isEmpty(filters.search)),
    [filters.appliedFilter, filters.search, getNoResults],
  );
  const columnOptions = useMemo(() => getColumnOption() as ColumnOption<TemplateContent>[], [getColumnOption]);

  return (
    <GenericItemsView
      key={context}
      items={items}
      viewType={selectedViewType}
      isLoading={isLoading}
      columnOptions={columnOptions}
      fetchData={fetchListView}
      dataCount={itemsCount}
      customHeaderContent={dropdown}
      renderSearch={() => (
        <SearchInput
          placeholder={`Search for ${searchInputPlaceholder[context] ?? context}`}
          onChange={handleSearchChange}
        />
      )}
      // @ts-ignore
      getFilterForm={() => <FilterForm />}
      applyFilter={handleApplyFilter}
      resetFilter={handleResetFilter}
      setReloadListItems={setReloadListItems}
      appliedFilter={filters.appliedFilter}
      filterOptions={filters.filterOptions}
      noResultsContent={noResults}
      filterOptionsLoading={filters.isLoading}
      getFilterOptions={handleFiltersFetch}
      renderCard={renderCard}
      sortOptions={gridSortOptions}
      defaultSortingColumnName={ContentColumns.Title}
      onSortChange={onSortChange}
      onViewTypeChange={onViewTypeChange}
      disableListViewButton={!renderCard}
      doNotLoadPersistentViewType={!renderCard}
      isAllDataLoaded={areAllLoaded}
      onLoad={isGridView ? () => fetchGrid(true) : noop}
      selectedIds={selected[context]}
      onSelectedItemChanged={onSelectedItemsChanged}
      onSelectedGridItemsChanged={onSelectedItemsChanged}
      onSelectionChanged={onSelectionChanged}
      listViewRtnEvents={rtnEvents}
      isSelectDisabled={() => disabled || (context !== ContentTypesEnum.Flows && selectedViewType === ViewType.LIST)}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const template = state.people.groupTemplate;
  const { content, filters } = template.content;

  return {
    ...content,
    filters,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    resetOverview: () => dispatch(reset()),
    resetItems: () => dispatch(resetItems()),
    setSelectedIds: bindAction(setSelected, dispatch),
    fetchContent: bindAction(fetchTemplateContent, dispatch),
    fetchFilters: bindAction(fetchTemplateContentFilters, dispatch),
    onSearchChanged: bindAction(setSearch, dispatch),
    applyFilter: bindAction(setAppliedFilter, dispatch),
    resetAppliedFilter: bindAction(resetAppliedFilter, dispatch),
    resetFilterState: bindAction(resetFilter, dispatch),
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PeopleContent);
export default ConnectedComponent;

const columnToParamMap: {
  [P in Lowercase<ContentColumns>]: string;
} = {
  title: "title",
  status: "isDraft",
  publisher: "publisher",
  "priority level": "priorityLevelId",
  objective: "goalObjectiveId",
};

const defaultGridSorting = ["dateCreated", SortingDirection.Descending] as const;
const gridSortOptions = [
  {
    text: "Most Recent",
    value: defaultGridSorting,
    default: true,
  },
  {
    text: "Last Modified",
    value: ["dateModified", SortingDirection.Descending],
  },
  {
    text: "Sorted A-Z",
    value: ["title", SortingDirection.Ascending],
  },
];

/* istanbul ignore next */
const getContentPropsMap = (
  _navigate: NavigateFunction,
  selected: { [key in ContentTypesEnum]?: number[] },
  contextMenuButtonHandlers: { onEditPriority: (item: FlowItem) => void; onClearPriority: (ids: number[]) => void },
  disabled: boolean,
) => ({
  [ContentTypesEnum.Flows]: {
    filterForm: FlowFilterForm,
    getColumnOption: () =>
      getFlowColumnOptions({
        url: `/${RouteNames.contentFlows}`,
        selectedIds: selected.Flows ?? [],
        contextMenuButtonHandlers,
        disabled,
      }),
    renderCard: (props: CardsViewerItem<FlowItem>) => (
      <PeopleFlowCard
        {...props}
        peopleType={PeopleType.Group}
        onEditPriority={() => contextMenuButtonHandlers.onEditPriority(props.item)}
        onRemove={() => contextMenuButtonHandlers.onClearPriority([props.item.id])}
        hasDeepLink={!disabled && props.item.hasAccess}
        disablePopupMenu={disabled}
      />
    ),
    getNoResults: (filtered: boolean) => (
      <FlowsNoResults
        byCriteria={filtered}
        title="You don't have any available Flows"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any Flows."
            : "Looks like you haven't added any Flows to this template."
        }
      />
    ),
  },
  [ContentTypesEnum.Uploaded]: {
    filterForm: VideosFilterForm,
    getColumnOption: () => getColumnOptions(`/${RouteNames.contentVideos}`, disabled),
    renderCard: (item: CardsViewerItem<VideoItem>) => (
      <PeopleVideoCard {...item} disablePopupMenu selectable={false} hasDeepLink={!disabled && item.item.hasAccess} />
    ),
    getNoResults: (filtered: boolean) => (
      <VideoAssetsNoResults
        filtered={filtered}
        title="You don't have any available videos"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any videos."
            : "Looks like you haven't added any videos to this template."
        }
      />
    ),
  },
  [ContentTypesEnum.Events]: {
    filterForm: EventFilterForm,
    getColumnOption: () => getColumnOptions(`/${RouteNames.contentEvents}`, disabled),
    renderCard: (item: CardsViewerItem<ContentItem>) => (
      <PeopleEventCard
        {...item}
        peopleType={PeopleType.Group}
        selectable={false}
        disablePopupMenu
        deepLink={!disabled}
      />
    ),
    getNoResults: (filtered: boolean) => (
      <EventsNoResults
        filtered={filtered}
        title="You don't have any available events"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any events."
            : "Looks like you haven't added any events to this template."
        }
      />
    ),
  },
  [ContentTypesEnum.PDFs]: {
    filterForm: PdfsFilterForm,
    getColumnOption: () => getColumnOptions(`/${RouteNames.contentPdfs}`, disabled),
    renderCard: (item: CardsViewerItem<ContentItem>) => (
      <PeoplePdfCard {...item} peopleType={PeopleType.Group} selectable={false} deepLink={!disabled} />
    ),
    getNoResults: (filtered: boolean) => (
      <PdfsNoResults
        filtered={filtered}
        title="You don't have any available PDFs"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any PDFs."
            : "Looks like you haven't added any PDFs to this template."
        }
      />
    ),
  },
  [ContentTypesEnum.Assessments]: {
    filterForm: AssessmentFilterForm,
    getColumnOption: () => getColumnOptions(`/${RouteNames.contentAssessments}`, disabled),
    getNoResults: (filtered: boolean) => (
      <AssessmentsNoResults
        filtered={filtered}
        title="You don't have any available assessments"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any assessments."
            : "Looks like you haven't added any assessments to this template."
        }
      />
    ),
    renderCard: undefined,
  },
  [ContentTypesEnum.Surveys]: {
    filterForm: SurveyFilterForm,
    getColumnOption: () => getColumnOptions(`/${RouteNames.contentSurveys}`, disabled),
    getNoResults: (filtered: boolean) => (
      <SurveysNoResults
        filtered={filtered}
        title="You don't have any available surveys"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any surveys."
            : "Looks like you haven't added any surveys to this template."
        }
      />
    ),
    renderCard: undefined,
  },
  [ContentTypesEnum.Embedded]: {
    filterForm: EmbeddedVideosFilterForm,
    getColumnOption: () => getColumnOptions(`/${RouteNames.contentEmbeddedVideos}`, disabled),
    renderCard: (item: CardsViewerItem<ContentItem>) => (
      <PeopleEmbeddedVideoCard {...item} peopleType={PeopleType.Group} deepLink={!disabled} />
    ),
    getNoResults: (filtered: boolean) => (
      <EmbeddedVideosNoResults
        filtered={filtered}
        title="You don't have any available embedded videos"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any embedded videos."
            : "Looks like you haven't added any embedded videos to this template."
        }
      />
    ),
  },
  [ContentTypesEnum.Scorm]: {
    filterForm: ScormFilterForm,
    getColumnOption: () => getColumnOptions(`/${RouteNames.contentScorm}`, disabled),
    renderCard: (item: CardsViewerItem<ContentAssignmentModelItem>) => (
      <PeopleScormCard {...item} peopleType={PeopleType.Group} deepLink />
    ),
    getNoResults: (filtered: boolean) => (
      <ScormsNoResults
        title="You don't have any available SCORM Packages"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any SCORM Packages."
            : "Looks like you haven't added any SCORM Packages to this template."
        }
        filtered={filtered}
      />
    ),
  },
  [ContentTypesEnum.Links]: {
    filterForm: LinkFilterForm,
    getColumnOption: () => getColumnOptions(`/${RouteNames.contentLinks}`, disabled),
    getNoResults: (filtered: boolean) => (
      <ExternalLinksNoResults
        filtered={filtered}
        title="You don't have any available links"
        description={
          disabled
            ? "Looks like the owner of this inherited group hasn't added any links."
            : "Looks like you haven't added any links to this template."
        }
      />
    ),
    renderCard: undefined,
  },
});

type ContentProps = ReturnType<typeof getContentPropsMap>[keyof ReturnType<typeof getContentPropsMap>];
type ContentMap = {
  [K in ContentTypesEnum]?: ContentProps;
};

const contentPropsPovider = (
  ctx: ContentTypesEnum,
  navigate: NavigateFunction,
  selected: { [key in ContentTypesEnum]?: number[] },
  contextMenuButtonHandlers: { onEditPriority: (item: FlowItem) => void; onClearPriority: (ids: number[]) => void },
  disabled: boolean,
): ContentProps => {
  const map: ContentMap = getContentPropsMap(navigate, selected, contextMenuButtonHandlers, disabled);
  return map[ctx]!;
};
